import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSuiClient, useSignAndExecuteTransaction } from "@mysten/dapp-kit";
import { ArrowLeft, Twitter, Send, Globe, ExternalLink, Sparkles, TrendingUp, Copy, Users, Share2, Activity, Coins, Lock, X, BadgeCheck, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, shortAddr } from "@/lib/api";
import { normalizeSocialUrl } from "@/lib/socials";
import { useWallet } from "@/context/WalletContext";
import { useDeployment } from "@/context/DeploymentContext";
import { getCurveState, migrateCurve, claimCreatorFees, payFee } from "@/lib/sui";
import { enablePush, getEndpoint } from "@/lib/push";
import { graduateToCetus } from "@/lib/cetus";
import { fmtNum, fmtSui, fmtPrice, fmtUsd, timeAgo } from "@/lib/format";
import CoinLogo from "@/components/CoinLogo";
import BondingCurveBar from "@/components/BondingCurveBar";
import SafetyBadges from "@/components/SafetyBadges";
import VerifiedCheck from "@/components/VerifiedCheck";
const PriceChart = React.lazy(() => import("@/components/PriceChart"));
import OnChainTradePanel from "@/components/OnChainTradePanel";
import TradesHolders from "@/components/TradesHolders";
import ShareCardModal from "@/components/ShareCardModal";
import { useLive } from "@/context/LiveContext";
import { liveDebounceMs, isHidden, useMediaQuery } from "@/lib/perf";

const DiscordIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true"><path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.2.36-.43.84-.59 1.23a18.3 18.3 0 0 0-3.94 0A8.3 8.3 0 0 0 11.44 3a19.74 19.74 0 0 0-3.76 1.37C3.77 9.33 3.02 14.13 3.3 18.86A19.9 19.9 0 0 0 9.1 21c.4-.55.76-1.14 1.06-1.75-.58-.22-1.14-.49-1.67-.8.14-.1.28-.21.41-.32 3.2 1.5 6.67 1.5 9.83 0 .14.11.27.22.41.32-.53.31-1.09.58-1.67.8.3.61.66 1.2 1.06 1.75a19.9 19.9 0 0 0 5.8-2.14c.33-5.47-.86-10.23-3.62-14.49ZM9.68 15.33c-.95 0-1.73-.87-1.73-1.95s.76-1.96 1.73-1.96c.97 0 1.75.88 1.73 1.96 0 1.08-.77 1.95-1.73 1.95Zm6.64 0c-.95 0-1.73-.87-1.73-1.95s.76-1.96 1.73-1.96c.97 0 1.75.88 1.73 1.96 0 1.08-.76 1.95-1.73 1.95Z"/></svg>
);

export default function OnChainCoinDetail({ coin: base }) {
  const client = useSuiClient();
  const { mutateAsync: signExec } = useSignAndExecuteTransaction();
  const { deployment } = useDeployment();
  const { connected, address } = useWallet();
  const { subscribe } = useLive();
  const [coin, setCoin] = useState(base);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [chart, setChart] = useState([]);
  const [candles, setCandles] = useState([]);
  const [trades, setTrades] = useState([]);
  const [devFirst, setDevFirst] = useState(null);
  const [holders, setHolders] = useState(0);
  const [topHolders, setTopHolders] = useState([]);
  const [devLock, setDevLock] = useState({ dev_locked_tokens: 0, dev_lock_days: 0 });
  const [suiUsd, setSuiUsd] = useState(null);
  const [tf, setTf] = useState("5m");
  const [shareOpen, setShareOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [sheetSide, setSheetSide] = useState("buy");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [migrator, setMigrator] = useState(null);
  const [grad, setGrad] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [verifyCfg, setVerifyCfg] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertBusy, setAlertBusy] = useState(false);

  const isCreator = connected && address && coin.creator && address.toLowerCase() === coin.creator.toLowerCase();
  const doClaimFees = async () => {
    if (!deployment?.package_id) return;
    setClaiming(true);
    try {
      await claimCreatorFees(signExec, client, { pkg: deployment.package_id, curveId: coin.curve_id, coinType: coin.coin_type });
      toast.success("Creator fees claimed to your wallet");
      loadState();
    } catch (e) {
      const m = String(e?.message || e);
      let msg;
      if (/E_ZERO|,\s*2\)|MoveAbort.*\b2\b/.test(m)) msg = "No fees to claim yet";
      else if (/E_NOT_CREATOR|,\s*9\)|MoveAbort.*\b9\b/.test(m)) msg = "Only the coin's creator can claim these fees";
      else if (/reject|cancel|denied/i.test(m)) msg = "Transaction rejected in wallet";
      else msg = "Claim failed: " + m.slice(0, 120);
      toast.error(msg);
    } finally { setClaiming(false); }
  };

  const triggeredRef = React.useRef(false);
  const loadState = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/onchain/${base.curve_id}`);
      setCoin(data);
      if (data && !data.migrated && (data.progress || 0) >= 1 && !triggeredRef.current) {
        triggeredRef.current = true;
        api.post(`/coins/onchain/${base.curve_id}/trigger`).catch(() => {});
      }
    } catch (e) {}
  }, [base.curve_id]);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/onchain/${base.curve_id}/activity`, { params: { package_id: base.package_id, creator: base.creator, decimals: base.decimals || 9, tf } });
      setChart(data.chart || []); setCandles(data.candles || []); setTrades(data.trades || []); setDevFirst(data.dev_first_buy); setHolders(data.holders || 0); setTopHolders(data.top_holders || []);
    } catch (e) {}
    try {
      const { data } = await api.get(`/coins/onchain/${base.curve_id}/vesting`, { params: { coin_type: base.coin_type, creator: base.creator, decimals: base.decimals || 9 } });
      setDevLock({ dev_locked_tokens: data.dev_locked_tokens || 0, dev_lock_days: data.dev_lock_days || 0 });
    } catch (e) {}
  }, [base.curve_id, base.package_id, base.creator, base.decimals, tf]);

  const loadComments = useCallback(async () => {
    try { const { data } = await api.get(`/coins/${base.curve_id}/comments`); setComments(data); } catch (e) {}
  }, [base.curve_id]);

  useEffect(() => { loadState(); loadActivity(); loadComments(); }, [loadState, loadActivity, loadComments]);
  useEffect(() => {
    const t = setInterval(() => { if (!isHidden()) { loadState(); loadActivity(); } }, 30000);
    return () => clearInterval(t);
  }, [loadState, loadActivity]);

  const liveTimer = React.useRef(null);
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type !== "trade" || msg.curve_id !== base.curve_id || isHidden()) return;
      if (liveTimer.current) clearTimeout(liveTimer.current);
      liveTimer.current = setTimeout(() => { loadState(); loadActivity(); }, Math.min(liveDebounceMs, 2000));
    });
  }, [subscribe, base.curve_id, loadState, loadActivity]);
  useEffect(() => { api.get("/sui-price").then(({ data }) => { if (data?.usd) setSuiUsd(data.usd); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/config").then(({ data }) => setMigrator(data?.migrator)).catch(() => {}); }, []);
  useEffect(() => { api.get("/verify/config").then(({ data }) => setVerifyCfg(data)).catch(() => {}); }, []);

  const doVerify = async () => {
    if (!connected) return toast.error("Connect your wallet first");
    if (!verifyCfg?.treasury) return toast.error("Verification not available right now");
    setVerifying(true);
    try {
      const amountMist = BigInt(Math.round((verifyCfg.fee_sui || 15) * 1e9));
      toast.info(`Confirm the ${verifyCfg.fee_sui || 15} SUI payment in your wallet…`);
      const digest = await payFee(signExec, client, { recipient: verifyCfg.treasury, amountMist });
      await api.post(`/coins/onchain/${coin.curve_id}/verify`, { curve_id: coin.curve_id, address, digest });
      toast.success("Verified! Your coin now has the blue check 💚");
      await loadState();
    } catch (e) {
      toast.error((e?.response?.data?.detail || e?.message || "Verification failed").slice(0, 140));
    } finally { setVerifying(false); }
  };

  const setPriceAlert = async (direction, pct) => {
    setAlertBusy(true);
    try {
      let endpoint = await getEndpoint();
      if (!endpoint) endpoint = await enablePush({ address, topics: ["graduation", "price"] });
      await api.post("/alerts/price", { endpoint, curve_id: coin.curve_id, direction, pct });
      toast.success(`Alert set: notify when $${coin.symbol} moves ${direction === "up" ? "+" : "−"}${pct}%`);
      setAlertOpen(false);
    } catch (e) {
      toast.error((e?.response?.data?.detail || e?.message || "Could not set alert").slice(0, 140));
    } finally { setAlertBusy(false); }
  };

  const doGraduate = async () => {
    if (!connected) return toast.error("Connect the migrator wallet");
    setGrad("Graduating curve — confirm in wallet (1/2)…");
    try {
      await migrateCurve(signExec, client, { pkg: coin.package_id, configId: deployment.config_id, curveId: coin.curve_id, coinType: coin.coin_type });
    } catch (e) {
      if (!String(e?.message || "").includes("MIGRATED")) { setGrad(""); return toast.error("Migrate failed: " + (e?.message || "").slice(0, 90)); }
    }
    setGrad("Creating Cetus pool — confirm in wallet (2/2)…");
    try {
      const digest = await graduateToCetus(signExec, { sender: address, creator: coin.creator, coinType: coin.coin_type, coinDecimals: coin.decimals || 9, gradPrice: coin.price_sui, suiMist: Math.floor((coin.real_sui_collected || 0) * 1e9) });
      toast.success(`🎓 Graduated · LP locked · fees → dev · ${digest.slice(0, 8)}…`);
      await loadState();
    } catch (e) {
      toast.error("Cetus pool failed: " + (e?.message || "").slice(0, 120));
    } finally { setGrad(""); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    if (!connected) return toast.error("Connect your Sui wallet first");
    try { await api.post(`/coins/${base.curve_id}/comments`, { address, text: comment }); setComment(""); loadComments(); }
    catch (e) { toast.error("Failed to post"); }
  };

  const scan = (id) => `https://suiscan.xyz/mainnet/object/${id}`;

  const socials = [
    coin.twitter && { key: "twitter", href: normalizeSocialUrl("twitter", coin.twitter), Icon: Twitter, label: "X", testid: "coin-twitter-link" },
    coin.telegram && { key: "telegram", href: normalizeSocialUrl("telegram", coin.telegram), Icon: Send, label: "Telegram", testid: "coin-telegram-link" },
    coin.website && { key: "website", href: normalizeSocialUrl("website", coin.website), Icon: Globe, label: "Website", testid: "coin-website-link" },
    coin.discord && { key: "discord", href: coin.discord, Icon: DiscordIcon, label: "Discord", testid: "coin-discord-link" },
  ].filter(Boolean);

  const StatTile = ({ icon: Icon, color, label, value, testid }) => (
    <div className="stat-pill" data-testid={testid}>
      <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: `${color}1f` }}><Icon className="w-4 h-4" style={{ color }} /></span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
        <div className="font-display font-bold text-sm leading-tight truncate">{value}</div>
      </div>
    </div>
  );

  const BondingCard = () => (
    <div className="card-surface p-5">
      <BondingCurveBar progress={coin.progress || 0} migrated={coin.migrated} current={coin.real_sui_collected} remaining={coin.sui_remaining} gradSui={coin.graduation_sui || 7500} />
      <p className="text-[11px] text-muted-foreground mt-3">Graduates to <b className="text-[#00E5FF]">Cetus</b> at {fmtNum(coin.graduation_sui || 7500)} SUI. Fee 1% (0.7% dev / 0.3% platform), enforced on-chain.</p>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-6 pb-28 lg:pb-6">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to board</Link>

      {coin.migrated && (
        <div className="rounded-2xl p-4 mb-5 border border-[#00E5FF]/40 flex items-center gap-3" style={{ background: "linear-gradient(90deg,rgba(0,229,255,0.1),rgba(30,198,46,0.08))" }}>
          <Sparkles className="w-6 h-6 text-[#00E5FF]" />
          <div><div className="font-display font-bold">Graduated to Cetus DEX 🎉</div><div className="text-xs text-muted-foreground">Graduation threshold reached — liquidity migrated to Cetus on-chain.</div></div>
        </div>
      )}

      {/* ---------- TOKEN HEADER BAR ---------- */}
      <div className="card-surface relative overflow-hidden p-5 sm:p-6 mb-5">
        <div className="hero-mesh opacity-20" aria-hidden="true" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="rounded-2xl p-[2px] shrink-0" style={{ background: "linear-gradient(135deg,#1EC62E,#7CF24E)" }}><CoinLogo coin={coin} size={66} /></div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-extrabold text-xl sm:text-2xl truncate">{coin.name}</h1>
                {coin.verified && <VerifiedCheck size={18} />}
                <span className="font-mono text-[#1EC62E] font-bold">${coin.symbol}</span>
                <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded text-black" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>Live · mainnet</span>
              </div>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                <Link to={`/u/${coin.creator}`} className="font-mono hover:text-[#1EC62E]" data-testid="coin-creator-link">by {shortAddr(coin.creator)}</Link>
                <span>{timeAgo(coin.created_at)}</span>
                <span className="inline-flex items-center gap-1" data-testid="holders-count"><Users className="w-3.5 h-3.5" /> {fmtNum(holders, 0)} holders</span>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(coin.coin_type); toast.success("Contract address copied"); }} data-testid="coin-ca-chip" className="mt-2 inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 rounded-md bg-white/5 border border-white/10 hover:border-[#1EC62E]/50 transition-colors" title={coin.coin_type}>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold shrink-0">CA</span>
                <span className="font-mono text-xs text-[#1EC62E] truncate">{coin.coin_type?.slice(0, 18)}…::{coin.symbol?.toUpperCase()}</span>
                <Copy className="w-3 h-3 text-muted-foreground shrink-0" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-6 sm:gap-8 lg:pl-6 lg:border-l border-white/10 shrink-0">
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Price</div>
              <div className="font-mono font-bold text-lg mt-0.5">{fmtPrice(coin.price_sui)} <span className="text-xs text-muted-foreground">SUI</span></div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Market cap</div>
              <div className="font-display font-extrabold text-lg mt-0.5" data-testid="coin-mc-usd">{suiUsd ? fmtUsd((coin.market_cap_sui || 0) * suiUsd) : "—"}</div>
            </div>
          </div>
        </div>

        {coin.description && <p className="relative text-sm text-muted-foreground mt-4 max-w-3xl">{coin.description}</p>}

        <SafetyBadges coin={coin} devLock={devLock} size="md" className="relative mt-4" />

        <div className="relative flex items-center gap-2 mt-4 pt-4 border-t border-white/5 flex-wrap">
          {socials.map((s) => (
            <a key={s.key} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={s.testid} className="grid place-items-center w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-muted-foreground hover:text-[#1EC62E] hover:border-[#1EC62E]/40 transition-colors">
              <s.Icon className="w-4 h-4" />
            </a>
          ))}
          <div className="ml-auto flex items-center gap-2 relative">
            {isCreator && !coin.verified && (
              <button onClick={doVerify} disabled={verifying} data-testid="get-verified-button" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#1EC62E]/40 text-[#1EC62E] text-xs font-bold hover:bg-[#1EC62E]/10 transition-colors disabled:opacity-50">
                <BadgeCheck className="w-3.5 h-3.5" /> {verifying ? "Verifying…" : `Get Verified · ${verifyCfg?.fee_sui ?? 15} SUI`}
              </button>
            )}
            <button onClick={() => setAlertOpen((v) => !v)} data-testid="price-alert-button" title="Price alert" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 text-muted-foreground text-xs font-bold hover:text-white hover:border-white/20 transition-colors">
              <Bell className="w-3.5 h-3.5" /> Alert
            </button>
            {alertOpen && (
              <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setAlertOpen(false)} data-testid="price-alert-overlay">
                <div className="w-full max-w-xs p-5 rounded-2xl border border-white/10 bg-background shadow-2xl animate-in fade-in zoom-in-95 duration-150" onClick={(e) => e.stopPropagation()} data-testid="price-alert-menu">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 font-display font-bold"><Bell className="w-4 h-4 text-[#1EC62E]" /> Price alert · <span className="text-[#1EC62E] font-mono">${coin.symbol}</span></div>
                    <button onClick={() => setAlertOpen(false)} data-testid="price-alert-close" className="p-1 rounded-lg bg-white/5 text-muted-foreground hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Notify me when price moves</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[["up", 50], ["up", 100], ["down", 25], ["down", 50]].map(([d, p]) => (
                      <button key={`${d}${p}`} disabled={alertBusy} onClick={() => setPriceAlert(d, p)} data-testid={`alert-preset-${d}-${p}`} className={`py-2.5 rounded-lg text-sm font-bold border transition-colors disabled:opacity-50 ${d === "up" ? "border-[#1EC62E]/30 text-[#1EC62E] hover:bg-[#1EC62E]/10" : "border-[#F43F5E]/30 text-[#F43F5E] hover:bg-[#F43F5E]/10"}`}>
                        {d === "up" ? "+" : "−"}{p}%
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-3 leading-snug">Needs notifications on. On iPhone, add SuiPads to your Home Screen first.</p>
                </div>
              </div>
            )}
            <button onClick={() => setShareOpen(true)} data-testid="share-button" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#1EC62E]/40 text-[#1EC62E] text-xs font-bold hover:bg-[#1EC62E]/10 transition-colors"><Share2 className="w-3.5 h-3.5" /> Share</button>
          </div>
        </div>
      </div>

      {/* ---------- CHART (main) + TRADE (side) ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 space-y-5">
          <div className="card-surface p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="eyebrow">Market cap chart</span>
              <div className="seg">
                {["1m", "5m", "1h"].map((t) => (
                  <button key={t} data-testid={`tf-${t}`} data-active={tf === t} onClick={() => setTf(t)} className="seg-tab !px-3 !py-1 text-xs font-mono">{t}</button>
                ))}
              </div>
            </div>
            <React.Suspense fallback={<div className="h-[380px] flex items-center justify-center text-muted-foreground text-sm">Loading chart…</div>}>
              <div className="min-h-[380px] lg:min-h-[420px]">
                <PriceChart candles={candles} data={chart} multiplier={(coin.total_supply || 1000000000) * suiUsd * (coin.display_scale || 1)} format={fmtUsd} />
              </div>
            </React.Suspense>
          </div>

          {/* Mobile: bonding curve (trade panel lives in the fixed bottom bar) */}
          <div className="lg:hidden">
            <BondingCard />
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="token-stats">
            <StatTile icon={Users} color="#1EC62E" label="Holders" value={fmtNum(holders, 0)} testid="stat-holders" />
            <StatTile icon={Activity} color="#1EC62E" label="Volume" value={`${fmtSui(coin.volume_sui || 0, 2)} SUI`} testid="stat-volume" />
            <StatTile icon={Coins} color={coin.dev_bought_first ? "#FFB800" : "#8aa0c0"} label="Dev first buy" value={coin.dev_bought_first ? (devFirst != null ? `${fmtSui(devFirst, 3)} SUI` : "YES") : "NO"} testid="stat-dev-first" />
            {devLock.dev_locked_tokens > 0
              ? <StatTile icon={Lock} color="#FFB800" label="Dev locked" value={`${fmtNum(devLock.dev_locked_tokens)} · ${devLock.dev_lock_days}d`} testid="stat-dev-locked" />
              : <StatTile icon={TrendingUp} color="#22C55E" label="Price" value={`${fmtPrice(coin.price_sui)}`} testid="stat-price" />}
          </div>

          {/* Creator fees */}
          {isCreator && (
            <div className="card-surface p-5 border border-[#FFB800]/40" style={{ background: "linear-gradient(90deg,rgba(255,184,0,0.06),transparent)" }} data-testid="creator-fees-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display font-bold text-[#FFB800]">Your creator fees</div>
                  <p className="text-xs text-muted-foreground mt-0.5">Your 0.7% share of every trade accrues here — claim it to your wallet anytime (never auto-sent).</p>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Claimable</div>
                  <div className="font-mono font-bold text-lg text-[#FFB800]" data-testid="claimable-fees">{fmtSui(coin.creator_fees_sui || 0, 4)} SUI</div>
                </div>
              </div>
              <button onClick={doClaimFees} disabled={claiming || !(coin.creator_fees_sui > 0)} data-testid="claim-fees-button" className="w-full mt-3 py-2.5 rounded-xl font-bold text-black disabled:opacity-40" style={{ background: "linear-gradient(90deg,#FFB800,#FF8A00)" }}>
                {claiming ? "Claiming…" : (coin.creator_fees_sui > 0 ? `Claim ${fmtSui(coin.creator_fees_sui, 4)} SUI` : "No fees to claim yet")}
              </button>
            </div>
          )}

          {!coin.migrated && (coin.progress || 0) >= 1 && (
            <div className="card-surface p-5 border border-[#00E5FF]/40" style={{ background: "linear-gradient(90deg,rgba(0,229,255,0.06),rgba(30,198,46,0.06))" }} data-testid="graduate-card">
              <div className="flex items-center gap-2 mb-1 font-display font-bold"><Sparkles className="w-4 h-4 text-[#00E5FF]" /> Graduating automatically</div>
              <p className="text-xs text-muted-foreground mb-1">Threshold reached 🎉 The auto-migrator will create the SUI/${coin.symbol} pool on Cetus with <b className="text-white">all remaining tokens + collected SUI</b>, <b className="text-white">permanently lock the LP</b>, and hand the <b className="text-white">fee-claim proof to the dev</b> — no action needed.</p>
              {migrator && address === migrator && (
                <button onClick={doGraduate} disabled={!!grad} data-testid="graduate-button" className="w-full mt-3 py-2.5 rounded-xl font-bold text-black disabled:opacity-50" style={{ background: "linear-gradient(90deg,#00E5FF,#1EC62E)" }}>
                  {grad || "Trigger now (migrator)"}
                </button>
              )}
            </div>
          )}
          {coin.migrated && coin.cetus_pool_digest && (
            <div className="card-surface p-4 border border-[#00E5FF]/40 flex items-center justify-between" data-testid="graduated-card">
              <span className="text-sm font-bold text-[#00E5FF]">🎓 Graduated · LP locked · fees → dev</span>
              <a href={`https://suiscan.xyz/mainnet/tx/${coin.cetus_pool_digest}`} target="_blank" rel="noreferrer" className="text-xs font-mono text-[#1EC62E] hover:underline flex items-center gap-1">pool tx <ExternalLink className="w-3.5 h-3.5" /></a>
            </div>
          )}

          {/* Trades / Holders / Vesting */}
          <TradesHolders coin={coin} />

          {/* Comments */}
          <div className="card-surface p-5">
            <div className="eyebrow mb-3">Comments</div>
            <div className="flex gap-2 mb-4">
              <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()} placeholder="Post a comment..." data-testid="comment-input" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#1EC62E]/60" />
              <button onClick={postComment} data-testid="comment-submit" className="btn-primary px-5 py-2 rounded-xl font-bold text-sm">Post</button>
            </div>
            <div className="space-y-3">
              {comments.map((c) => <div key={c.id} className="text-sm"><span className="font-mono text-xs text-[#1EC62E]">{shortAddr(c.address)}</span><span className="text-muted-foreground text-xs ml-2">{timeAgo(c.ts)}</span><p className="mt-0.5">{c.text}</p></div>)}
              {comments.length === 0 && <div className="py-4 text-center text-muted-foreground text-sm">No comments yet.</div>}
            </div>
          </div>
        </div>

        {/* ---------- DESKTOP SIDEBAR: trade + bonding ---------- */}
        <div className="hidden lg:block lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            {isDesktop && <OnChainTradePanel coin={coin} onTraded={loadState} />}
            <BondingCard />
            <a href={scan(coin.curve_id)} target="_blank" rel="noreferrer" className="card-surface p-3 flex items-center justify-center gap-2 text-xs font-mono text-[#1EC62E]">View curve on Suiscan <ExternalLink className="w-3.5 h-3.5" /></a>
          </div>
        </div>
      </div>

      {/* ---------- MOBILE FIXED TRADING BOTTOM BAR ---------- */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 glass border-t border-white/10 px-3 pt-2.5" style={{ paddingBottom: "max(0.7rem, env(safe-area-inset-bottom))" }} data-testid="mobile-trade-bar">
        {coin.migrated ? (
          <button onClick={() => { setSheetSide("buy"); setTradeOpen(true); }} data-testid="mobile-bar-cetus" className="w-full py-3 rounded-xl font-display font-bold text-black active:scale-[0.99] transition-transform" style={{ background: "linear-gradient(90deg,#00E5FF,#1EC62E)" }}>Trade on Cetus</button>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            <button onClick={() => { setSheetSide("buy"); setTradeOpen(true); }} data-testid="mobile-bar-buy" className="py-3 rounded-xl font-display font-bold text-[#04121f] active:scale-[0.98] transition-transform" style={{ background: "linear-gradient(90deg,#16C784,#00E0A3)" }}>Buy</button>
            <button onClick={() => { setSheetSide("sell"); setTradeOpen(true); }} data-testid="mobile-bar-sell" className="py-3 rounded-xl font-display font-bold text-white active:scale-[0.98] transition-transform" style={{ background: "linear-gradient(90deg,#F43F5E,#FF007A)" }}>Sell</button>
          </div>
        )}
      </div>

      {/* Mobile expanded trade sheet (full panel, single-mount only while open) */}
      {!isDesktop && tradeOpen && (
        <div className="lg:hidden fixed inset-0 z-[90] flex items-end bg-black/70 backdrop-blur-sm" onClick={() => setTradeOpen(false)}>
          <div className="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl border-t border-white/10 bg-background p-4 animate-in slide-in-from-bottom" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()} data-testid="mobile-trade-sheet">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><CoinLogo coin={coin} size={32} /><span className="font-display font-bold">Trade <span className="text-[#1EC62E] font-mono">${coin.symbol}</span></span></div>
              <button onClick={() => setTradeOpen(false)} data-testid="mobile-trade-close" className="p-1.5 rounded-lg bg-white/5 text-muted-foreground hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <OnChainTradePanel key={sheetSide} coin={coin} onTraded={loadState} initialSide={sheetSide} />
          </div>
        </div>
      )}

      {shareOpen && <ShareCardModal coin={coin} suiUsd={suiUsd} onClose={() => setShareOpen(false)} />}
    </div>
  );
}
